import { useMemo, useState, type ReactNode } from 'react';
import { Checkbox as AriaCheckbox, CheckboxGroup as AriaCheckboxGroup, CheckboxGroupStateContext } from 'react-aria-components';
import { Check, Minus } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import { FieldFoot, FieldLabel, cx } from './parts';
import { TextField } from './TextField';
import type { CheckboxGroupOption, CheckboxGroupProps, CheckboxProps } from './types';
import './styles';

function Mark() {
  return (
    <span className="fm-box" aria-hidden="true">
      <Check size={13} strokeWidth={3} className="fm-box-check" />
      <Minus size={13} strokeWidth={3} className="fm-box-minus" />
    </span>
  );
}

function Text({ children, description, meta }: { children: ReactNode; description?: ReactNode; meta?: ReactNode }) {
  return (
    <>
      <span className="fm-check-text">
        <span>{children}</span>
        {description ? <small>{description}</small> : null}
      </span>
      {meta !== undefined && meta !== null ? <span className="fm-check-meta">{meta}</span> : null}
    </>
  );
}

/** Одиночный флажок: рамка 18 px, знак выбора (а не только цвет), «—» для частичного выбора. */
export function Checkbox({ isSelected, onChange, children, description, isIndeterminate, isDisabled, className }: CheckboxProps) {
  return (
    <AriaCheckbox className={cx('fm-check', className)} isSelected={isSelected} onChange={onChange} isIndeterminate={isIndeterminate} isDisabled={isDisabled}>
      <Mark />
      <Text description={description}>{children}</Text>
    </AriaCheckbox>
  );
}

const matches = (option: CheckboxGroupOption, query: string) => option.name.toLocaleLowerCase().includes(query) || (option.description ?? '').toLocaleLowerCase().includes(query);

/**
 * Список флажков: «Выбрать все · N» (действует на найденные, со знаком
 * «частично»), поиск по названию над списком, прокрутка внутри `maxHeight` —
 * шапка списка остаётся на месте. Значение — массив id; скрытые поиском
 * пункты в нём сохраняются.
 */
export function CheckboxGroup<V extends string = string>({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, className, value, onChange, options, selectAll = false, maxHeight, searchable = false, summary, searchPlaceholder, emptyText }: CheckboxGroupProps<V>) {
  const t = useT();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLocaleLowerCase();
  const visible = useMemo(() => (needle ? options.filter((option) => matches(option, needle)) : options), [options, needle]);
  const selected = useMemo(() => new Set<string>(value), [value]);
  const selectable = visible.filter((option) => !option.isDisabled);
  const chosen = selectable.filter((option) => selected.has(option.id)).length;
  const all = selectable.length > 0 && chosen === selectable.length;
  const some = chosen > 0 && !all;
  const total = options.length;

  const toggleAll = (on: boolean) => {
    const ids = new Set<string>(selectable.map((option) => option.id));
    onChange(on ? [...value, ...selectable.filter((option) => !selected.has(option.id)).map((option) => option.id)] : value.filter((id) => !ids.has(id)));
  };

  const summaryNode = summary === undefined ? (selectAll || searchable ? t(FM.selectedOf, { count: value.length, total }) : null) : summary;
  const showTools = selectAll || searchable || summaryNode !== null;

  return (
    <AriaCheckboxGroup
      className={cx('fm-group', 'fm-field', className)}
      value={value}
      onChange={(next) => onChange(next as V[])}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      aria-label={label ? undefined : ariaLabel}
    >
      <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
      <div className="fm-list">
        {showTools ? (
          <div className="fm-list-tools">
            {searchable ? <TextField size="sm" type="search" aria-label={t(FM.groupSearch)} placeholder={searchPlaceholder ?? t(FM.searchPlaceholder)} value={query} onChange={setQuery} isDisabled={isDisabled} /> : null}
            {selectAll || summaryNode !== null ? (
              <div className="fm-list-head">
                {selectAll ? (
                  // «Выбрать все» — не пункт группы: без сброса контекста он стал бы одним из значений.
                  <CheckboxGroupStateContext.Provider value={null}>
                    <AriaCheckbox className="fm-check" isSelected={all} isIndeterminate={some} onChange={toggleAll} isDisabled={isDisabled || selectable.length === 0}>
                      <Mark />
                      <Text>{needle ? t(FM.selectFound, { count: selectable.length }) : t(FM.selectAll, { count: selectable.length })}</Text>
                    </AriaCheckbox>
                  </CheckboxGroupStateContext.Provider>
                ) : null}
                {summaryNode !== null ? <span className="fm-list-count">{summaryNode}</span> : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="fm-list-scroll" style={maxHeight ? { maxHeight } : undefined}>
          {visible.map((option) => (
            <AriaCheckbox key={option.id} className="fm-check" value={option.id} isDisabled={option.isDisabled}>
              <Mark />
              <Text description={option.description} meta={option.meta}>
                {option.name}
              </Text>
            </AriaCheckbox>
          ))}
          {visible.length === 0 ? <div className="fm-list-empty">{emptyText ?? t(FM.nothingFound)}</div> : null}
        </div>
      </div>
      <FieldFoot description={description} errorMessage={errorMessage} isInvalid={isInvalid} />
    </AriaCheckboxGroup>
  );
}
